import { useCallback, useEffect, useState } from "react";
import { Download, Undo2 } from "lucide-react";
import { api, fmtDate, money } from "@/lib/api";
import { PageHead, EmptyState } from "@/components/Bits";
import { downloadCsv } from "@/lib/csv";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function ReturnsReport() {
  const [studios, setStudios] = useState([]);
  const [studioId, setStudioId] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState({ rows: [], count: 0, total_value: 0, total_serials: 0 });

  useEffect(() => {
    api.get("/studios").then(({ data }) => setStudios(data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const params = {};
    if (studioId !== "all") params.studio_id = studioId;
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    api.get("/reports/returns", { params }).then(({ data }) => setData(data)).catch(() => {});
  }, [studioId, from, to]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const rows = data.rows.map((r) => ({
      "Returned on": fmtDate(r.returned_at),
      Studio: r.studio_name,
      Type: r.order_type === "b2b" ? "B2B" : "Studio",
      Items: r.items_summary,
      "Value (INR)": r.value,
      Reason: r.return_reason,
      "Returned by": r.returned_by,
      "Voided serials": r.voided_serials.join(" | "),
      "Replacement raised": r.replaced ? "Yes" : "No",
    }));
    downloadCsv(`returns-report-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="returns-report-page">
      <PageHead title="Returns Report" subtitle="Returned material orders with reason, value and voided serials for reconciliation">
        <Button variant="outline" data-testid="returns-export-btn" onClick={exportCsv} disabled={!data.rows.length}>
          <Download className="mr-2 h-4 w-4" /> Export CSV
        </Button>
      </PageHead>

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-4">
        <div>
          <Label>Studio</Label>
          <Select value={studioId} onValueChange={setStudioId}>
            <SelectTrigger data-testid="returns-studio-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All studios</SelectItem>
              {studios.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>From</Label>
          <Input type="date" data-testid="returns-from" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <Label>To</Label>
          <Input type="date" data-testid="returns-to" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="flex items-end">
          {(from || to || studioId !== "all") && (
            <Button variant="ghost" data-testid="returns-clear" onClick={() => { setStudioId("all"); setFrom(""); setTo(""); }}>
              Clear
            </Button>
          )}
        </div>
      </div>

      <div className="mb-5 grid grid-cols-3 gap-3">
        {[["Returns", data.count], ["Total value", money(data.total_value)], ["Serials voided", data.total_serials]].map(([l, v]) => (
          <Card key={l} className="border-brand-line shadow-none">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">{l}</p>
              <p className="mt-1 text-2xl font-semibold text-brand-ink" data-testid={`returns-stat-${l}`}>{v}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-brand-line shadow-none">
        <CardContent className="overflow-x-auto p-0">
          {data.rows.length === 0 ? (
            <EmptyState icon={Undo2} title="No returns found" hint="No returned orders match these filters." />
          ) : (
            <Table data-testid="returns-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Returned</TableHead>
                  <TableHead>Studio</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Voided serials</TableHead>
                  <TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                  <TableRow key={r.id} data-testid={`returns-row-${r.id}`}>
                    <TableCell className="whitespace-nowrap">{fmtDate(r.returned_at)}</TableCell>
                    <TableCell>
                      {r.studio_name}
                      {r.order_type === "b2b" && <Badge className="ml-2 border-0 bg-slate-100 text-slate-600">B2B</Badge>}
                    </TableCell>
                    <TableCell className="text-slate-600">{r.items_summary}</TableCell>
                    <TableCell className="text-right">{money(r.value)}</TableCell>
                    <TableCell>{r.return_reason}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {r.voided_serials.length ? r.voided_serials.join(", ") : "—"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-slate-600">
                      {r.returned_by}
                      {r.replaced && <Badge className="ml-2 border-0 bg-emerald-50 text-emerald-700">replaced</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
